'use client'

import React from 'react'

import { cn } from '@/lib/utils'
import Button from '@/components/livecontact/Button'
import FieldError from '@/components/livecontact/FieldError'
import LcIcon from '@/components/livecontact/icons'

type Props = {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  placeholder?: string
  buttonText?: string
  loading?: boolean
  autoFocus?: boolean
  /** Inline validation message — rendered under the field, no shake, no toast. */
  error?: string
  /**
   * `stacked` is the landing hero: a 48px radius-16 field with the button under it.
   * `inline` is the compact 40px pill with the button beside it, for tighter surfaces.
   */
  variant?: 'inline' | 'stacked'
  classInput?: string
  className?: string
}

/**
 * URL capture: a tinted globe chip, the field, and the primary action — the one place a
 * website URL is typed. The button's `loading` crossfade covers the submit state; errors show
 * as a `FieldError` line so the layout never jumps or shakes.
 */
const UrlPillForm = ({
  value,
  onChange,
  onSubmit,
  placeholder = 'Enter your website URL',
  buttonText = 'See the magic',
  loading,
  autoFocus,
  error,
  variant = 'inline',
  classInput,
  className,
}: Props) => {
  const stacked = variant === 'stacked'

  return (
    <form
      className={cn('flex w-full flex-col', stacked ? 'items-stretch' : 'items-center', className)}
      onSubmit={(e) => {
        e.preventDefault()
        if (!loading) onSubmit()
      }}
      noValidate
    >
      <div
        className={cn(
          stacked
            ? 'flex w-full flex-col gap-4'
            : 'flex w-full items-center justify-center gap-3 md:flex-col md:gap-3'
        )}
      >
        <label
          className={cn(
            'flex items-center bg-surface',
            'transition-[box-shadow] duration-[160ms] ease-out-strong',
            stacked
              ? 'h-12 w-full gap-3 rounded-[16px] pl-[7px] pr-[13px]'
              : 'h-10 w-[400px] gap-2.5 rounded-full pl-1 pr-4 md:w-full',
            error
              ? 'shadow-[0_1px_1px_rgba(1,54,151,0.04),0_0_0_1px_#D33,0_0_0_4px_rgba(221,51,51,0.12)]'
              : cn(
                  stacked
                    ? 'shadow-[0_1px_1px_rgba(1,54,151,0.04),0_0_0_1px_rgba(1,54,151,0.08)]'
                    : 'shadow-[0_1px_1px_rgba(1,54,151,0.04),0_0_0_1px_rgba(0,0,0,0.08)]',
                  // The light ring is a near-black tint, which disappears on a dark surface —
                  // dark mode inverts it to a white hairline of the same weight.
                  'dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_0_0_1px_rgba(255,255,255,0.18)]',
                  'focus-within:shadow-[0_1px_1px_rgba(1,54,151,0.04),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)]',
                  'dark:focus-within:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.24)]'
                ),
            loading && 'opacity-70'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-full text-brand',
              'size-8 border border-white/10 bg-gradient-to-b from-[rgba(14,80,231,0.1)] to-[rgba(70,123,244,0.1)]'
            )}
          >
            <LcIcon name="globe" size={stacked ? 20 : 18} />
          </span>
          <input
            type="text"
            inputMode="url"
            autoComplete="url"
            autoFocus={autoFocus}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            aria-label="Your website URL"
            aria-invalid={error ? true : undefined}
            disabled={loading}
            className={cn(
              'min-w-0 flex-1 bg-transparent text-[16px] font-medium leading-none tracking-[-0.2px] text-ink outline-none placeholder:text-[#757575] disabled:cursor-wait',
              classInput
            )}
          />
        </label>

        <Button
          type="submit"
          size="lg"
          loading={loading}
          leadingIcon={<LcIcon name="sparkles" size={stacked ? 20 : 18} />}
          className={cn(
            'shrink-0',
            stacked
              ? 'h-12 w-full rounded-[16px] text-[16px]'
              : 'rounded-full px-5 text-[15px] md:w-full'
          )}
        >
          {buttonText}
        </Button>
      </div>

      <FieldError message={error} className={cn('mt-2', !stacked && 'text-center')} />
    </form>
  )
}

export default UrlPillForm
